import { useEffect } from 'react';

import { applyDevPermissionGrant, applyHsqeMockRoleGrant } from '@/api/common/auth/devPermissionGrant';
import { useWhoAmI } from '@/api/common/auth/hooks';
import { setTelemetryUser } from '@/lib/telemetry';
import { useAuthStore } from '@/stores/useAuthStore';
import { useAuth as useClerkAuth } from '@clerk/react';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { setClerkAuth, setUser, setLoadingUserData, clearAuth } = useAuthStore();
  const { data: whoAmIData, isLoading: isWhoAmILoading, error } = useWhoAmI();

  useEffect(() => {
    setClerkAuth(isLoaded, isSignedIn);

    if (!isLoaded) return;

    if (!isSignedIn) {
      clearAuth();
      setTelemetryUser(undefined);
      return;
    }

    if (isWhoAmILoading) {
      setLoadingUserData(true);
      return;
    }

    if (error || !whoAmIData) {
      setUser(null);
      setTelemetryUser(undefined);
    } else {
      // Decorate the real whoAmI user with the full HSQE permission set when either
      // non-production grant applies: the DEV-only dev-server grant, or the UAT-only
      // VITE_HSQE_MOCK grant for a user who holds HSQE-S. Both are no-ops in every real
      // production build, and both keep the real permission path — the store receives real
      // permission strings, not a blanket bypass.
      setUser(applyHsqeMockRoleGrant(applyDevPermissionGrant(whoAmIData.Security.User)));
      setTelemetryUser(whoAmIData.Security.User.Email);
    }
  }, [isLoaded, isSignedIn, whoAmIData, isWhoAmILoading, error, setClerkAuth, setUser, setLoadingUserData, clearAuth]);

  return <>{children}</>;
};
